/**
 * Pure helpers for the school management tabs (ADM-8): the student roster
 * filter/sort, document labels and framework summaries. No React here, so
 * they are unit-tested directly.
 */
import type { SchoolFramework } from '../../types/schoolFramework';
import { frameworkDisplayName } from '../../utils/frameworkName';
import type {
  PendingInvitation,
  SchoolDocument,
  Student,
  StudentFilter,
  StudentSort,
} from './schoolManagementTypes';

type TFn = (key: string, vars?: Record<string, unknown>) => string;

/**
 * Enrolled students plus pending invitations (as student-like rows with a
 * negative id), filtered and sorted for the Students tab.
 */
export function filterAndSortStudents(
  students: Student[],
  pendingInvites: PendingInvitation[],
  studentFilter: StudentFilter,
  studentSort: StudentSort,
): Student[] {
  // Convert pending invitations to student-like objects
  const invitedStudents: Student[] = pendingInvites.map((invite) => ({
    id: -invite.id, // Negative ID to distinguish from real students
    email: invite.email,
    full_name: invite.full_name,
    is_active: false,
    created_at: invite.created_at,
    is_invited: true,
    invitation_id: invite.id,
    invitation_expires: invite.invitation_expires,
  }));

  // Merge students and invitations
  const allStudents = [...students, ...invitedStudents];

  // Apply filter
  let filtered = allStudents;
  if (studentFilter === 'active') {
    filtered = allStudents.filter((s) => s.is_active && !s.is_invited);
  } else if (studentFilter === 'inactive') {
    filtered = allStudents.filter((s) => !s.is_active && !s.is_invited);
  } else if (studentFilter === 'invited') {
    filtered = allStudents.filter((s) => s.is_invited);
  }

  // Apply sort
  const sorted = [...filtered].sort((a, b) => {
    switch (studentSort) {
      case 'name':
        return (a.full_name || a.email).localeCompare(b.full_name || b.email);
      case 'activity': {
        const aTime = a.last_activity ? new Date(a.last_activity).getTime() : 0;
        const bTime = b.last_activity ? new Date(b.last_activity).getTime() : 0;
        return bTime - aTime; // Most recent first
      }
      case 'transcripts':
        return (b.transcripts_total || 0) - (a.transcripts_total || 0);
      case 'score':
        return (b.avg_icf_score || 0) - (a.avg_icf_score || 0);
      default:
        return 0;
    }
  });

  return sorted;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Display name of a framework in the UI language (one helper app-wide, I18N-R09). */
export { frameworkDisplayName };

export function frameworkNamesById(frameworks: SchoolFramework[], language: string): Map<number, string> {
  const names = new Map<number, string>();
  for (const framework of frameworks) {
    names.set(framework.id, frameworkDisplayName(framework, language));
  }
  return names;
}

/**
 * "Method" shown on a document card: the linked frameworks' names when it has
 * any, otherwise the translated coaching method.
 */
export function documentMethodLabel(
  doc: SchoolDocument,
  frameworkNameById: Map<number, string>,
  t: TFn,
): string {
  const linkedIds = Array.isArray(doc.linked_framework_ids)
    ? doc.linked_framework_ids.filter((id): id is number => Number.isInteger(id))
    : [];

  if (linkedIds.length > 0) {
    return linkedIds
      .map((id) => frameworkNameById.get(id) || `#${id}`)
      .join(', ');
  }

  switch (doc.coaching_method) {
    case 'custom_framework':
      return t('schoolManagement.documentsSection.methodCustomFramework');
    case 'framework_linked':
      return t('schoolManagement.documentsSection.methodLinkedFramework');
    case 'school_document':
      return t('schoolManagement.documentsSection.methodSchoolDocument');
    case 'GROW model':
      return t('schoolManagement.documentsSection.methodGrowModel');
    default:
      return doc.coaching_method || t('schoolManagement.documentsSection.methodUnknown');
  }
}

/** Counts summarised under each framework card. */
export function frameworkExtractionTotals(framework: SchoolFramework) {
  const steps = framework.steps || [];
  return {
    subSteps: steps.reduce((sum, step) => sum + (step.sub_steps?.length || 0), 0),
    concepts: steps.reduce((sum, step) => sum + (step.concepts?.length || 0), 0),
    positive: steps.reduce((sum, step) => sum + (step.positive_markers?.length || 0), 0),
    negative: steps.reduce((sum, step) => sum + (step.negative_markers?.length || 0), 0),
    questions: steps.reduce((sum, step) => sum + (step.sample_questions?.length || 0), 0),
  };
}
